import { type VMSCameraId } from '@solinkcorp/api-types';
import { type RectCoordinates } from 'components/MotionGrid/DwellTimeRoi';
import { type Point } from 'components/VideoPlayer/components/AI/types';
import { type AbstractRequestor } from '../shared/AbstractRequestor';
import { expiresIn, isDatePast } from '../shared/AppCache';
import { type IParams } from '../shared/IRequestor';
import {
  type EventTrackingZone,
  type HistoricalEventResponse,
  type PeopleAnalyticsObjectKind,
  type VisionAnalyticsSystemRequirement,
  type VMSCamera,
  type VMSCrossingEventLine,
  type VMSLineCrossing,
  type VMSMultiPeopleTrackingCameras,
  type VMSPeopleTrackingCamera,
  type VMSPolygonSearch,
  type VMSSegment,
} from './vmsTypes';

/** ONVIF PTZVector's PanTilt component — pan/tilt are always sent together as one 2D vector. */
export type PtzPanTilt = { pan: number; tilt: number };

export type PtzPosition = { pan: number; tilt: number; zoom: number };

/**
 * ONVIF PTZ move-in-progress state for one axis group. Backend enum has an `Unknown(String)`
 * variant in addition to the idle/moving units; modeled here as `{ unknown: string }` per
 * serde's standard external-tagging for a newtype variant, but unconfirmed against a real
 * sample (only `'idle'` has been observed so far).
 */
export type PtzMoveState = 'idle' | 'moving' | { unknown: string };

export type PtzMoveStatusInfo = {
  panTilt: PtzMoveState | null;
  zoom: PtzMoveState | null;
};

/**
 * Response shape of ONVIF GetStatus, as returned by the camera-configurator service.
 * `position`/`moveStatus` are each `Option<T>` on the Rust side, so both are nullable —
 * callers must not assume `position` is present.
 */
export type PtzStatus = {
  position: PtzPosition | null;
  moveStatus: PtzMoveStatusInfo | null;
  error: string | null;
  utcTime: string;
};

/** A preset position predefined on the camera. `token`/`name` are each `Option<String>` on the Rust side. */
export type PtzPreset = {
  token?: string;
  name?: string;
  position?: PtzPosition;
};

export class VmsApi {
  constructor(
    private readonly requestor: AbstractRequestor,
    private readonly cloudOnly: boolean,
    private readonly cloudVmsHost: string,
  ) {}

  async listVideos(params: {
    tenantId: string;
    deviceId: string;
    camId: string;
    streamId: string;
    start: number;
    end: number;
    merge?: boolean;
    motiongrid?: boolean;
    forceCloud?: boolean;
    forceCloudStream?: boolean;
    targetStart?: number;
    targetEnd?: number;
    signal?: AbortSignal;
  }) {
    const {
      tenantId,
      deviceId,
      camId,
      streamId,
      start,
      end,
      merge,
      motiongrid,
      forceCloud,
      forceCloudStream,
      targetStart,
      targetEnd,
      signal,
    } = params;

    let url = '';
    let source = '';

    const query = new URLSearchParams({
      start: `${start}`,
      end: `${end}`,
    });

    query.set('setCookie', '0');

    if (targetStart !== undefined) {
      query.set('targetStart', `${targetStart}`);
    }
    if (targetEnd !== undefined) {
      query.set('targetEnd', `${targetEnd}`);
    }

    if (merge) {
      query.set('merge', 'true');
    }

    if (this.cloudOnly || forceCloud || forceCloudStream) {
      const streams = forceCloudStream ? `streams/${streamId}/` : '';
      url = `${
        this.cloudVmsHost
      }/tenants/${tenantId}/devices/${deviceId}/cameras/${camId}/${streams}playlist.json?${query.toString()}`;
      source = 'CLOUD';
    } else {
      url = _listVideosUrl(camId, streamId, start, end, motiongrid);
      source = 'LOCAL';
    }

    return this.requestor
      .get({
        url,
        expires: isDatePast({ date: end }) ? expiresIn(1).years() : 0,
        signal,
      })
      .then((data: any) => ({
        source,
        stream: data.offset.stream,
        segments: fixSegmentTimestamps(data.videos),
      }));
  }

  pushVideo(camId: string, streamId: string, start: number, end: number, eventType: string) {
    return this.requestor.post({
      url: `/push_to_cloud?type=${eventType || 'default'}`,
      data: { camId, streamId, start, end },
      params: { setCookie: 0 },
    });
  }

  shareVideo(params: {
    eventId: string;
    eventType?: string;
    camId: string;
    streamId: string;
    start: number;
    end: number;
    priority: number;
    metadata: { id?: string; fillGaps?: boolean };
    merge: boolean;
  }) {
    const { eventId, eventType, camId, streamId, start, end, priority, merge, metadata } = params;
    const body = {
      eventId,
      camId,
      streamId,
      start: new Date(start).getTime(),
      end: new Date(end).getTime(),
      metadata: JSON.stringify(metadata),
      priority,
    };

    const queryParams: IParams | undefined = {
      share: 'true',
      type: eventType || 'default',
    };

    queryParams.setCookie = '0';

    if (merge) {
      queryParams.merge = 'true';
    }

    return this.requestor.post({
      url: '/push_to_cloud',
      data: body,
      params: queryParams,
    });
  }

  downloadVideo(
    camID: string,
    streamID: string,
    start: string | number,
    end: string | number,
    format: string,
    name: string,
    overlayText: string,
  ) {
    return this.requestor.post({
      url: '/download',
      data: { camID, streamID, start, end, format, name, overlayText },
      params: { setCookie: 0 },
    });
  }

  getDownloadStatus(alias: string) {
    return this.requestor.get({
      url: `/download/${alias}.json`,
      params: { setCookie: 0 },
    });
  }

  generateDownloadVideoLink(alias: string, filename: string) {
    const params = new URLSearchParams({ setCookie: '0' });
    if (filename) {
      params.set('filename', filename);
    }

    const param = `?${params.toString()}`;

    return `/download/${alias}.mp4${param}`;
  }

  getCameraJson(camId: string) {
    return this.requestor.get<{ success: boolean; camera: VMSCamera }>({
      url: _cameraJsonUrl(camId),
      params: { setCookie: 0 },
    });
  }

  putCameraMotion(
    camId: string,
    motionParams: { enabled: boolean; roi: string; eventThreshold?: 1 | 2 | 3 },
  ) {
    let url = _cameraMotionUrl(camId);

    url += `?setCookie=0`;

    return this.requestor.put<{ success: boolean }>({
      url,
      data: {
        camera_id: camId,
        camera: {
          motion: motionParams,
        },
      },
    });
  }

  /**
   * ONVIF ContinuousMove — starts/updates a pan/tilt/zoom move at the given normalized
   * velocity ([-1, 1] per axis), addressed to a specific stream on the device. `panTilt` is
   * a single 2D vector (mirroring ONVIF's PTZVector) — pass `null` to leave pan/tilt alone
   * rather than commanding a velocity of 0 on both axes. `zoom` is independently nullable.
   */
  ptzContinuousMove(
    camId: string,
    streamId: string,
    panTilt: PtzPanTilt | null,
    zoom: number | null,
  ) {
    return this.requestor.post<{ success: boolean }>({
      url: _cameraPtzUrl(camId, streamId, 'move/continuous'),
      data: { panTilt, zoom },
    });
  }

  /** ONVIF Stop — halts whatever ContinuousMove is currently in progress, on both the pan/tilt and zoom axes. */
  ptzStop(camId: string, streamId: string) {
    return this.requestor.post<{ success: boolean }>({
      url: _cameraPtzUrl(camId, streamId, 'stop'),
      data: { panTilt: true, zoom: true },
    });
  }

  /**
   * ONVIF AbsoluteMove — moves directly to the given pan/tilt/zoom position ([-1, 1] per
   * axis), addressed to a specific stream on the device. `panTilt` is a single 2D vector
   * (mirroring ONVIF's PTZVector) — pass `null` to leave pan/tilt at their current position
   * rather than commanding them to 0, so e.g. a zoom-only call doesn't also recenter
   * pan/tilt. `zoom` is independently nullable.
   */
  ptzAbsoluteMove(
    camId: string,
    streamId: string,
    panTilt: PtzPanTilt | null,
    zoom: number | null,
  ) {
    return this.requestor.post<{ success: boolean }>({
      url: _cameraPtzUrl(camId, streamId, 'move/absolute'),
      data: { panTilt, zoom },
    });
  }

  /**
   * ONVIF RelativeMove — translates by the given pan/tilt/zoom delta ([-1, 1] per axis),
   * addressed to a specific stream on the device. `panTilt` is a single 2D vector
   * (mirroring ONVIF's PTZVector) — pass `null` to leave pan/tilt alone. `zoom` is
   * independently nullable.
   */
  ptzRelativeMove(
    camId: string,
    streamId: string,
    panTilt: PtzPanTilt | null,
    zoom: number | null,
  ) {
    return this.requestor.post<{ success: boolean }>({
      url: _cameraPtzUrl(camId, streamId, 'move/relative'),
      data: { panTilt, zoom },
    });
  }

  /**
   * ONVIF GetStatus — returns the camera's current pan/tilt/zoom position ([-1, 1] per
   * axis) plus in-progress move status, addressed to a specific stream on the device.
   */
  ptzGetStatus(camId: string, streamId: string) {
    return this.requestor.get<PtzStatus>({
      url: _cameraPtzUrl(camId, streamId, 'status'),
    });
  }

  /** ONVIF GetPresets — lists the presets predefined on the camera for a specific stream. */
  ptzGetPresets(camId: string, streamId: string) {
    return this.requestor.get<PtzPreset[]>({
      url: _cameraPtzUrl(camId, streamId, 'presets'),
    });
  }

  /** ONVIF GotoPreset — moves the camera to a previously-defined preset position. */
  ptzGotoPreset(camId: string, streamId: string, presetToken: string) {
    return this.requestor.post<{ success: boolean }>({
      url: _cameraPtzUrl(camId, streamId, 'presets/goto'),
      data: { presetToken },
    });
  }

  /** ONVIF GotoHomePosition — moves the camera to its configured home position. */
  ptzGotoHome(camId: string, streamId: string) {
    return this.requestor.post<{ success: boolean }>({
      url: _cameraPtzUrl(camId, streamId, 'home'),
    });
  }

  putCameraAi({
    camId,
    aiParams,
  }: {
    camId: string;
    aiParams?: {
      enabled: boolean;
      detectionType: string;
      roi?: [number, number, number, number] | null | undefined;
    };
  }) {
    let url = _cameraAiUrl(camId);

    url += `?setCookie=0`;

    return this.requestor.put({
      url,
      data: aiParams,
    });
  }

  postCameraAiDwellTime(
    camId: string,
    dtParams: {
      enabled: boolean;
      rois: { roi_id?: string; metadata?: string; roi: RectCoordinates }[];
    },
  ) {
    let url = _cameraAiDwellTimeUrl(camId);

    url += `?setCookie=0`;

    return this.requestor.post({
      url,
      // fixme: the detection type is probably not needed / causing issues here. Double check.
      data: { detectionType: 'dwellTime', ...dtParams },
    });
  }

  updateCameraName(camId: string, newCameraName: string) {
    let url = _camerasUrl(camId);

    url += `?setCookie=0`;

    return this.requestor.put({
      url,
      data: { name: newCameraName },
    });
  }

  async testAccess() {
    const res: any = await this.requestor.request({
      method: 'GET',
      url: '/id',
      options: { timeout: 1000, params: { setCookie: 0 } },
    });

    return res.data;
  }

  /**
   * Get the counts of people crossing a line from two directions.
   * @param dirPoint  It is a point outside the line segment, indicating the direction. The API will return the
   *                  trajectory based on the direction of this point. The one was on the same direction of the
   *                  line will have `dir` value of 1. The one was on the opposite direction of the line will
   *                  have `dir` value of -1.
   */
  async getPeopleCounts({
    cameraId,
    streamId,
    startTime,
    endTime,
    linePoints,
    dirPoint,
    countsOnly = 0,
    nPoints = 0,
    objectKinds,
  }: {
    cameraId: string;
    streamId: string;
    startTime: number;
    endTime: number;
    linePoints: Point[];
    dirPoint: Point;
    countsOnly?: 0 | 1;
    nPoints?: number;
    objectKinds?: PeopleAnalyticsObjectKind;
  }): Promise<VMSLineCrossing> {
    const { x: x0, y: y0 } = linePoints[0];
    const { x: x1, y: y1 } = linePoints[1];
    const { x: dirX, y: dirY } = dirPoint;
    const url = `/vms/ai/line_crossing/intersection?cam_id=${cameraId}&stream_id=${streamId}&start=${startTime}&end=${endTime}&n_points=${nPoints}&x0=${x0}&y0=${y0}&x1=${x1}&y1=${y1}&dir_x=${dirX}&dir_y=${dirY}&counts_only=${countsOnly}&object_kinds=${objectKinds}`;
    const res: VMSLineCrossing = await this.requestor.get({
      url,
      params: { setCookie: 0 },
    });
    return res;
  }

  async getPolygonSearchIntersections({
    cameraId,
    streamId,
    startTime,
    endTime,
    points,
    includePaths,
    minTimeMs,
    fullPaths = 0,
    objectKinds,
    includeBoxes = false,
  }: {
    cameraId: string;
    streamId: string;
    startTime: number;
    endTime: number;
    points: Point[];
    includePaths?: boolean; // use false for stats requests with time range > 24 hours
    minTimeMs?: number; // min time to consider an intersected path as 'dwell'. Anything lower than that are considered passthrough
    fullPaths?: number;
    objectKinds?: PeopleAnalyticsObjectKind[];
    includeBoxes?: boolean;
  }): Promise<VMSPolygonSearch> {
    let url = `/vms/ai/line_crossing/roi_search?cam_id=${cameraId}&stream_id=${streamId}&start=${startTime}&end=${endTime}&n=${points.length}`;

    // Default to both kinds when unspecified — API otherwise returns persons only.
    if (objectKinds && objectKinds.length > 0) {
      url += `&object_kinds=${objectKinds.join(',')}`;
    } else {
      url += `&object_kinds=person,vehicle`;
    }

    if (fullPaths) {
      url += `&full_paths=1`;
    }

    if (includeBoxes) {
      url += `&include_boxes=1`;
    }

    const pointsString = points.reduce(
      (pointsStr, { x, y }, index) => (pointsStr += `&x${index}=${x}&y${index}=${y}`),
      '',
    );

    url += pointsString;

    if (includePaths !== undefined) {
      url += `&include_paths=${includePaths ? 1 : 0}`;
    }

    if (minTimeMs) {
      url += `&min_time_ms=${minTimeMs}`;
    }

    const res: VMSPolygonSearch = await this.requestor.get({
      url,
      params: { setCookie: 0 },
    });

    return res;
  }

  async putPolygonEventConfig({
    enabled,
    cameraId,
    streamId,
    zones,
    multiCam,
  }: {
    enabled: boolean;
    cameraId: string;
    streamId: string;
    zones: EventTrackingZone[];
    multiCam?: boolean;
    objectKinds?: PeopleAnalyticsObjectKind;
  }) {
    const url = `/vms/ai/line_crossing/config/${cameraId}${multiCam ? '?multicam=1' : ''}`;
    //NOTE: cannot update objectKinds, need to delete and create new config
    const res = await this.requestor.put({
      url,
      data: {
        enabled,
        cam_id: cameraId,
        stream_id: streamId,
        dwell_event_polygons: zones,
      },
    });
    return res;
  }

  async getPolygonEventConfig(
    cameraId: string,
    multiCam?: boolean,
  ): Promise<{ zones: EventTrackingZone[]; activeCameraIds: string[] }> {
    const url = `/vms/ai/line_crossing/config${multiCam ? '?multicam=1' : ''}`;
    const res = await this.requestor.get<VMSMultiPeopleTrackingCameras>({
      url,
    });
    return {
      zones: res[cameraId]?.dwell_event_polygons ?? [],
      activeCameraIds: Object.keys(res),
    };
  }

  async getLoiteringCamerasConfig() {
    const url = `/vms/ai/line_crossing/config?multicam=1`;
    const res: VMSMultiPeopleTrackingCameras = await this.requestor.get({
      url,
    });
    return res;
  }

  /**
   * Get the camera configured for people analysis.
   * Before interlink version 122 only supports single camera.
   * For interlink version 122 and above, up to 3 cameras are supported with the query paramerter included in url.
   * See line crossing module readme for more details: https://github.com/SolinkCorp/connect-ai/blob/master/app/code/modules/line_crossing/README.md
   */
  async getPeopleTrackingCameraConfig(multiCam?: boolean) {
    const url = `/vms/ai/line_crossing/config${multiCam ? '?multicam=1' : ''}`;
    const res: VMSPeopleTrackingCamera = await this.requestor.get({
      url,
    });
    return res;
  }

  async getVisionAnalyticsSystemRequirement() {
    const url = `/vms/ai/line_crossing/config?get_max_cameras=1`;
    const res: VisionAnalyticsSystemRequirement = await this.requestor.get({
      url,
    });
    return res;
  }

  async getHistoricalEvents(start: number, end: number): Promise<HistoricalEventResponse[]> {
    const url = `/ai/modules/line_crossing/events?start=${start}&end=${end}`;
    const res: HistoricalEventResponse[] = await this.requestor.get({
      url,
    });
    return res;
  }

  /**
   * Add config for the camera for people analysis.
   * Before interlink version 122 only supports single camera.
   * For interlink version 122 and above, up to 3 cameras are supported with the query paramerter included in url.
   */
  async addPeopleTrackingCameraConfig({
    camId,
    streamId,
    enabled,
    event_lines,
    multiCam,
    objectKinds,
  }: {
    camId: string;
    streamId: string;
    enabled: boolean;
    event_lines?: VMSCrossingEventLine[];
    multiCam: boolean;
    objectKinds: PeopleAnalyticsObjectKind[];
  }) {
    const url = `/vms/ai/line_crossing/config${multiCam ? '?multicam=1' : ''}`;
    const res = await this.requestor.post({
      url,
      data: {
        cam_id: camId,
        stream_id: streamId,
        enabled,
        event_lines,
        object_kinds: objectKinds,
      },
    });
    return res;
  }

  /**
   * Update config for a single people tracking camera
   * Only supported for interlink 122 and up
   *
   */
  async updatePeopleTrackingCameraConfig({
    camId,
    streamId,
    enabled,
    event_lines,
  }: {
    camId: string;
    streamId?: string;
    enabled?: boolean;
    event_lines?: VMSCrossingEventLine[];
  }) {
    const url = `/vms/ai/line_crossing/config/${camId}`;
    const res = await this.requestor.put({
      url,
      data: {
        stream_id: streamId,
        enabled,
        event_lines,
      },
    });
    return res;
  }

  /**
   * Delete config for a single people tracking camera
   * Only supported for interlink 122 and up
   *
   */
  async deletePeopleTrackingCameraConfig({ camId }: { camId: VMSCameraId }) {
    const url = `/vms/ai/line_crossing/config/${camId}`;
    const res = await this.requestor.delete({ url });
    return res;
  }

  lightCheckin() {
    return this.requestor
      .post({
        url: `/checkin/api/light_checkin`,
      })
      .catch((err) => {
        // log, but don't fail if light checkin didn't succeed
        console.error('Error during light checkin', err);
      });
  }
}

function _camerasUrl(camId: string) {
  return `/cameras/${camId}`;
}

function _streamsUrl(camId: string, streamId: string) {
  return `${_camerasUrl(camId)}/streams/${streamId}`;
}

function _listVideosUrl(
  camId: string,
  streamId: string,
  start: number,
  end: number,
  motiongrid = false,
) {
  return `${_streamsUrl(
    camId,
    streamId,
  )}/list_videos?start=${start}&end=${end}&with_cloud_info=1&motiongrid=${motiongrid}`;
}

function _cameraJsonUrl(camId: string) {
  return `${_camerasUrl(camId)}/json`;
}

function _cameraMotionUrl(camId: string) {
  return `${_camerasUrl(camId)}/motion`;
}

function _cameraPtzUrl(camId: string, streamId: string, action: string) {
  return `${_camerasUrl(camId)}/${streamId}/ptz/${action}`;
}

function _cameraAiUrl(camId: string) {
  return `${_camerasUrl(camId)}/ai`;
}

function _cameraAiDwellTimeUrl(camId: string) {
  // FIXME: why is the `/vms/` prefix of the url required (otherwise getting 404)?
  return `vms/${_camerasUrl(camId)}/ai/dwell_time`;
}

const fixSegmentTimestamps = (data: Array<{ start: string; end: string }>) => {
  return data.map((datum) => ({
    ...datum,
    start: parseInt(datum.start, 10),
    end: parseInt(datum.end, 10),
  })) as VMSSegment[];
};
